/**
 * Forgot Password Component
 * Allows users to reset their password by providing email and phone number
 */

import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
} from "@mui/material";
import { Email, Phone, ArrowBack, Security } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../utils/textUtils";
import { useSecureAuth } from "../../contexts/SecureAuthContext";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { resetPassword } = useSecureAuth();
  
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  
  // 2FA states
  const [show2FA, setShow2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [sessionId, setSessionId] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email || !phoneNumber) {
      setError("Please fill in both email and phone number");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Validate phone number format (should be (000)-0000-000)
    const phoneRegex = /^\(\d{3}\)-\d{3}-\d{4}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setError("Please enter a valid phone number in format (000)-0000-000");
      return;
    }

    try {
      setLoading(true);
      
      // Step 1: Send 2FA verification code to phone number
      // Use forgot-password endpoint that accepts email and phoneNumber
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const response = await fetch(`${apiUrl}/api/auth/forgot-password/initiate-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          phoneNumber,
          method: "sms",
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Failed to send verification code');
      }

      // Store session ID and show 2FA form
      setSessionId(data.sessionId);
      setShow2FA(true);
      setMessage("Verification code sent to your phone");
    } catch (error: any) {
      setError("Failed to send verification code: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit verification code");
      return;
    }

    try {
      setLoading(true);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
      
      // Step 2: Verify 2FA code using forgot password verify endpoint
      const verifyResponse = await fetch(`${apiUrl}/api/auth/forgot-password/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          verificationCode,
          email,
          phoneNumber,
          method: "sms",
        }),
      });

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyData.success) {
        throw new Error(verifyData.error || verifyData.message || 'Verification failed');
      }

      // Step 3: After 2FA verified, send password reset email using Firebase directly
      // This uses the same logic as before - Firebase sends the email directly
      await resetPassword(email);

      setMessage("Password reset email sent! Please check your inbox for instructions.");
      
      // Redirect after a delay
      setTimeout(() => {
        navigate("/researcher/auth");
      }, 3000);
    } catch (error: any) {
      setError("Failed: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/researcher/auth")}
            sx={{ alignSelf: "flex-start", mb: 2 }}
          >
            Back to Login
          </Button>

          <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            Enter your email address and phone number to receive password reset instructions
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
              {message}
            </Alert>
          )}

          {!show2FA ? (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                margin="normal"
                required
                disabled={loading}
                placeholder="(000)-0000-000"
                helperText="US phone number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: !phoneNumber && (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        US phone number
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Verification Code"}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handle2FAVerification} sx={{ width: "100%" }}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Security sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
                <Typography variant="h6" component="div">
                  SMS Verification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We've sent a 6-digit verification code to your phone
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").substring(0, 6);
                  setVerificationCode(value);
                }}
                margin="normal"
                required
                disabled={loading}
                inputProps={{
                  maxLength: 6,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? "Verifying..." : "Verify Code"}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setShow2FA(false);
                  setVerificationCode("");
                  setMessage("");
                }}
                disabled={loading}
              >
                Back
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;

